// frontend/src/components/supplier/ItemSuppliersDialog.jsx
//
// One item, its suppliers — the item-side twin of the Supplier catalog screen
// (pages/SupplierCatalog.jsx, one supplier and its items). Opened from the
// Item setup grid's Supplier cell.
//
// Why it exists: the grid cell can only ELECT a default among the suppliers
// an item already has (backend refuses an unassigned supplier once relations
// exist — serializers.py `_route_supplier_relation_writes`). Here a merchant
// who thinks item-first adds a second supplier beside the item, with the same
// writes the supplier-first catalog makes:
//
//   assign       POST /api/supplier-catalog-items/ — one relation, with the
//                cost / SKU / lead time the merchant typed. The item's FIRST
//                supplier becomes its default and, under Model B, its cost of
//                record follows; when that would move a cost the item already
//                holds the endpoint answers `requires_confirmation` and writes
//                nothing, and the page's shared cost gate asks the question.
//   make default POST …/set-default/ — same gate, same modal.
//   remove       DELETE …/<id>/ — the backend may auto-promote a survivor; the
//                toast names it so the flip is never silent.
//
// Per-relation TERMS are not edited here. Their editor is the catalog grid
// (bulk, with the ERP MOQ carve-out); a row's "Edit terms" opens it on this
// item. One editor per rule.
//
// CREATING A SUPPLIER HERE. The supplier the merchant wants may not exist
// yet — on a fresh install none does. "Create supplier" opens THE Create
// supplier dialog (CreateSupplierDialog.jsx, the same one Supplier setup
// opens) over this one. The new row is then picked in the assign form, so the
// merchant carries on with cost and terms.
// The created row is kept in state so the picker can name it before the
// supplier list re-reads.
//
// Every write is immediate — this is not part of the grid's dirty/save cycle
// — and the page's grid re-reads its rows after each one, which is why the
// page opens this dialog only through its unsaved-changes guard.
//
// ERP-owned stores (an active ERP connector owns item-supplier links) get the
// list read-only: no assign, no default flip, no remove — the backend 409s all
// three, so the controls are withheld rather than offered and refused.

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DialogErrorBanner from '../common/DialogErrorBanner';

import { ICON, PencilSimple, Plus, Trash, X } from '../../icons';
import ConfirmDialog from '../common/ConfirmDialog';
import CreateSupplierDialog from './CreateSupplierDialog';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { useWriteGate } from '../../hooks/useWriteGate';
import { useFormatters } from '../../hooks/useFormatters';
import { useItemSupplierLinks } from '../../hooks/useItemSupplierLinks';
import { effectiveCostLabel } from './effectiveCost';
import { useSuppliers } from '../../hooks/useReferenceData';
import {
  useCreateSupplierCatalogItem,
  useDeleteSupplierCatalogItem,
  useSetDefaultSupplierCatalogItem,
} from '../../hooks/useSupplierCatalogItemMutations';
import { translateRequestError } from '../../queries/api';
import { supplierCatalogPath } from '../../pages/supplierCatalogLinks';
import { useTabs } from '../../tabs/useTabs';
import DialogHeading from '../common/DialogHeading';
import '../../styles/dialog-form.css';
import { lengthLimits } from '../../constants/orgFieldLimits';
import { showToast } from '../../components/common/appToastBus';

const EMPTY_FORM = { supplier: null, cost: '', supplierSku: '', leadTime: '' };
// Mirrors api.models.ItemSupplier.supplier_sku (see lengthLimits).
const ASSIGN_FIELD_MAX = { supplierSku: 64 };

/**
 * @param {object} props
 * @param {{itemId: number|string, label: string, itemCode: string} | null} props.item
 *   the item whose suppliers are shown; `null` keeps the dialog closed.
 *   `label` names it in the title and toasts (utils/itemLabel.formatItemLabel);
 *   `itemCode` filters the catalog to it on "Edit terms".
 * @param {() => void} props.onClose
 * @param {{request: Function}} props.costGate  the host's `useResolutionGate()`,
 *   whose <CurrencyChangeConfirmModal> the host already mounts
 */
export default function ItemSuppliersDialog({ item, onClose, costGate }) {
  const { t } = useTranslation();
  const { openTab } = useTabs();
  const { formatNumber, parseNumber } = useFormatters();
  const { confirm, dialogProps } = useConfirmDialog();

  const open = item != null;
  const itemId = item?.itemId ?? null;
  const { links, isPending, isError } = useItemSupplierLinks(itemId);
  const { data: suppliers, error: suppliersError } = useSuppliers();
  // With an ERP owning item-supplier links the backend 409s every relation
  // write (item_supplier_link_erp_managed), so the controls are withheld — the
  // same gate as the Supplier catalog (SupplierCatalog.jsx).
  const linkGate = useWriteGate('item_supplier_links');
  // Creating a supplier is a supplier-master write, gated on its own domain
  // (supplier_create_erp_managed).
  const canCreateSupplier = useWriteGate('suppliers').mayWrite;

  const createMutation = useCreateSupplierCatalogItem();
  const setDefaultMutation = useSetDefaultSupplierCatalogItem();
  const deleteMutation = useDeleteSupplierCatalogItem();

  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState(null);
  // Whether the Create supplier dialog is open over this one, and the row it
  // made — kept for the life of this dialog, so a reopen before the supplier
  // list re-reads still offers it.
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState(null);
  const busy = createMutation.isPending || setDefaultMutation.isPending || deleteMutation.isPending;
  const assignLengths = lengthLimits(form, ASSIGN_FIELD_MAX, t);

  // Suppliers the item does not have yet — the only ones "Add" may name. The
  // relation list is authoritative for "already linked"; the store list for
  // "exists" — plus a row created here, offered at once while the list
  // catches up on its re-read.
  const linkedIds = useMemo(() => new Set(links.map((l) => l.supplier.id)), [links]);
  const known = useMemo(
    () => (created && !suppliers.some((s) => s.supplier_id === created.supplier_id)
      ? [created, ...suppliers]
      : suppliers),
    [suppliers, created],
  );
  const addable = useMemo(
    () => known
      .filter((s) => !linkedIds.has(s.supplier_id))
      .sort((a, b) => (a.supplier_name || '').localeCompare(b.supplier_name || '')),
    [known, linkedIds],
  );
  const pickerShown = known.length > 0 && (addable.length > 0 || isPending);
  const addCurrency = form.supplier?.default_currency?.currency_code || '';

  const close = useCallback(() => {
    setForm(EMPTY_FORM);
    setError(null);
    setCreateOpen(false);
    onClose();
  }, [onClose]);

  const fail = useCallback((err, fallbackKey) => {
    console.error('ItemSuppliersDialog:', err?.code, err?.message || err);
    setError(translateRequestError(err, t, fallbackKey));
  }, [t]);

  // ── Assign ──
  const handleAssign = useCallback(async () => {
    if (!form.supplier) return;
    setError(null);
    const supplier = form.supplier;
    // Only what was typed. A blank cost is a relation with no price — a real
    // state ("this supplier's price is not known yet"), not a zero. A typed
    // value that is not a number is refused HERE: `parseNumber` answers NaN,
    // which JSON carries as null, and the server would then store an unpriced
    // relation and this dialog would report it as added with the price.
    const fields = {};
    if (form.cost.trim() !== '') {
      const cost = parseNumber(form.cost);
      if (!Number.isFinite(cost) || cost < 0) { setError(t('msg.invalidNumber')); return; }
      fields.cost = cost;
    }
    if (form.supplierSku.trim() !== '') fields.supplier_sku = form.supplierSku.trim();
    if (form.leadTime.trim() !== '') {
      const days = parseNumber(form.leadTime);
      if (!Number.isInteger(days) || days < 0) { setError(t('msg.invalidNumber')); return; }
      fields.lead_time_days = days;
    }
    const vars = { itemId, supplierId: supplier.supplier_id, fields };
    const assigned = () => {
      setForm(EMPTY_FORM);
      showToast(t('itemSuppliers.messages.assigned', { supplier: supplier.supplier_name }), 'success');
    };
    try {
      const data = await createMutation.mutateAsync(vars);
      if (data?.requires_confirmation) {
        // Model B: this relation becomes the item's default and its price the
        // item's cost. The shared modal asks the one adopt confirmation and
        // builds the fragment that answers it; the retry carries that fragment
        // verbatim. A retry still gated is NOT a success.
        const confirmed = await costGate.request({
          changeContext: data.change_context,
          affectedItems: data.affected_items,
          onConfirm: async (confirmBody) => {
            try {
              const retry = await createMutation.mutateAsync({ ...vars, confirm: confirmBody });
              if (retry?.requires_confirmation) return { error: t('msg.saveFailed') };
              assigned();
            } catch (err) {
              return { error: translateRequestError(err, t, 'itemSuppliers.messages.assignFailed') };
            }
          },
        });
        if (!confirmed) showToast(t('currencyChange.cancelledAlert'), 'info');
        return;
      }
      assigned();
    } catch (err) {
      fail(err, 'itemSuppliers.messages.assignFailed');
    }
  }, [form, itemId, parseNumber, createMutation, costGate, t, fail]);

  // ── Create ──
  // The Create supplier dialog has already told the merchant; here the new
  // row is picked for them, because the next step is its cost and terms.
  const handleCreated = useCallback((data) => {
    setCreated(data);
    setCreateOpen(false);
    setError(null);
    setForm({ ...EMPTY_FORM, supplier: data });
  }, []);

  // ── Make default ──
  const handleSetDefault = useCallback(async (link) => {
    if (link.is_default) return;
    setError(null);
    const promoted = () => {
      showToast(t('itemSuppliers.messages.setDefaultSuccess', { supplier: link.supplier.name }), 'success');
    };
    try {
      const data = await setDefaultMutation.mutateAsync({ id: link.id });
      if (data?.requires_confirmation) {
        const confirmed = await costGate.request({
          changeContext: data.change_context,
          affectedItems: data.affected_items,
          onConfirm: async (confirmBody) => {
            try {
              const retry = await setDefaultMutation.mutateAsync({ id: link.id, confirm: confirmBody });
              if (retry?.requires_confirmation) return { error: t('msg.saveFailed') };
              promoted();
            } catch (err) {
              return { error: translateRequestError(err, t, 'itemSuppliers.messages.setDefaultFailed') };
            }
          },
        });
        if (!confirmed) showToast(t('currencyChange.cancelledAlert'), 'info');
        return;
      }
      promoted();
    } catch (err) {
      fail(err, 'itemSuppliers.messages.setDefaultFailed');
    }
  }, [setDefaultMutation, costGate, t, fail]);

  // ── Remove ──
  const handleRemove = useCallback(async (link) => {
    const supplier = link.supplier.name;
    const ok = await confirm({
      title: t('itemSuppliers.messages.removeTitle'),
      message: link.is_default
        ? t('itemSuppliers.messages.removeMessageDefault', { supplier })
        : t('itemSuppliers.messages.removeMessage', { supplier }),
      confirmLabel: t('btn.remove'),
      variant: 'danger',
    });
    if (!ok) return;
    setError(null);
    try {
      const result = await deleteMutation.mutateAsync({ id: link.id });
      const next = result?.promoted_default;
      showToast(
        next
          ? t('itemSuppliers.messages.removedPromoted', { supplier, promoted: next.supplier_name })
          : t('itemSuppliers.messages.removed', { supplier }),
        'success',
      );
    } catch (err) {
      fail(err, 'itemSuppliers.messages.removeFailed');
    }
  }, [confirm, deleteMutation, t, fail]);

  // "Edit terms" hands the merchant to the catalog tab and closes this dialog
  // on the way. The shell hides an overlay with its own tab
  // (tabs/TabOverlayScope.jsx), so a dialog left open would not float over the
  // new tab — it would be sitting here, still open, when the merchant came
  // back. That is not what "edit it over there" means.
  const leaveFor = useCallback((path) => {
    openTab(path);
    close();
  }, [openTab, close]);

  const leadTimeCell = (link) => {
    if (link.lead_time_days != null) return `${link.lead_time_days} ${t('common.days')}`;
    return (
      <Tooltip title={t('itemSuppliers.leadTimeInherited')}>
        <span style={{ color: 'var(--color-text-muted)' }}>
          {link.supplier_sla_lead_time != null
            ? `${link.supplier_sla_lead_time} ${t('common.days')}`
            : '—'}
        </span>
      </Tooltip>
    );
  };

  // The price this relation really uses — its own, or the item's cost of
  // record converted into the supplier's currency, in muted ink like the
  // lead time beside it. The assign form below still writes the OWN cost;
  // this cell shows what that leaves in use.
  const costCell = (link) => {
    const label = effectiveCostLabel(link, { formatNumber });
    if (label === null) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
    return link.cost_inherited === true
      ? <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      : label;
  };

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : close} className="dialog-form">
        <DialogTitle>
          <DialogHeading title={t('itemSuppliers.title')} subtitle={item?.label} />
          <IconButton onClick={close} size="small" disabled={busy} aria-label={t('btn.close')}>
            <X size={ICON.xl} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('itemSuppliers.intro')}
          </Typography>

          {linkGate.erpVendor && <Alert severity="info">{t('itemSuppliers.erpManaged')}</Alert>}
          {(isError || suppliersError) && (
            <Alert severity="error">{t('itemSuppliers.loadFailed')}</Alert>
          )}

          {isPending ? (
            <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
          ) : links.length === 0 ? (
            !isError && (
              <Typography variant="body2" color="text.secondary">
                {t('itemSuppliers.empty')}
              </Typography>
            )
          ) : (
            <Table size="small" aria-label={t('itemSuppliers.tableLabel')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('itemSuppliers.columns.supplier')}</TableCell>
                  <TableCell>{t('itemSuppliers.columns.supplierSku')}</TableCell>
                  <TableCell align="right">{t('itemSuppliers.columns.cost')}</TableCell>
                  <TableCell align="right">{t('itemSuppliers.columns.leadTime')}</TableCell>
                  <TableCell>{t('itemSuppliers.columns.default')}</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {links.map((link) => (
                  <TableRow key={link.id} data-testid={`item-supplier-${link.supplier.id}`}>
                    <TableCell>{link.supplier.name}</TableCell>
                    <TableCell>{link.supplier_sku || <span style={{ color: 'var(--color-text-muted)' }}>—</span>}</TableCell>
                    <TableCell align="right">{costCell(link)}</TableCell>
                    <TableCell align="right">{leadTimeCell(link)}</TableCell>
                    <TableCell>
                      {link.is_default ? (
                        <Chip size="small" color="primary" label={t('itemSuppliers.defaultBadge')} />
                      ) : (
                        linkGate.mayWrite && (
                          <Button
                            size="small"
                            variant="text"
                            disabled={busy}
                            loading={setDefaultMutation.isPending && setDefaultMutation.variables.id === link.id}
                            onClick={() => handleSetDefault(link)}
                          >
                            {t('itemSuppliers.makeDefault')}
                          </Button>
                        )
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={t('itemSuppliers.editTerms')}>
                        <IconButton
                          size="small"
                          onClick={() => leaveFor(
                            supplierCatalogPath(link.supplier.id, item.itemCode),
                          )}
                          aria-label={t('itemSuppliers.editTerms')}
                        >
                          <PencilSimple size={ICON.md} />
                        </IconButton>
                      </Tooltip>
                      {linkGate.mayWrite && (
                        <Tooltip title={t('btn.remove')}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={busy}
                              onClick={() => handleRemove(link)}
                              aria-label={t('itemSuppliers.removeFor', { supplier: link.supplier.name })}
                            >
                              <Trash size={ICON.md} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {linkGate.mayWrite && (
            <>
              <Typography className="dialog-form-section">{t('itemSuppliers.assign.section')}</Typography>
              {known.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('itemSuppliers.assign.noSuppliers')}
                </Typography>
              ) : !pickerShown ? (
                <Typography variant="body2" color="text.secondary">
                  {t('itemSuppliers.assign.allLinked')}
                </Typography>
              ) : (
                <>
                  <Box className="form-row-2col">
                    <Autocomplete
                      options={addable}
                      value={form.supplier}
                      getOptionLabel={(o) => o.supplier_name || ''}
                      isOptionEqualToValue={(o, v) => o.supplier_id === v?.supplier_id}
                      onChange={(_, v) => setForm((prev) => ({ ...prev, supplier: v }))}
                      renderInput={(params) => (
                        <TextField {...params} label={t('itemSuppliers.assign.supplier')} size="small" />
                      )}
                      size="small"
                      className="flex-1"
                      disabled={busy}
                    />
                    <TextField
                      label={t('itemSuppliers.assign.cost')}
                      value={form.cost}
                      onChange={(e) => setForm((prev) => ({ ...prev, cost: e.target.value }))}
                      size="small"
                      className="flex-1"
                      disabled={busy || !form.supplier}
                      helperText={addCurrency
                        ? t('itemSuppliers.assign.costHelp', { ccy: addCurrency })
                        : t('itemSuppliers.assign.costHelpNoSupplier')}
                      slotProps={{
                        input: addCurrency ? {
                          startAdornment: <InputAdornment position="start">{addCurrency}</InputAdornment>,
                        } : undefined,
                      }}
                    />
                  </Box>
                  <Box className="form-row-2col">
                    <TextField
                      label={t('itemSuppliers.assign.supplierSku')}
                      value={form.supplierSku}
                      onChange={(e) => setForm((prev) => ({ ...prev, supplierSku: e.target.value }))}
                      size="small"
                      className="flex-1"
                      disabled={busy}
                      {...assignLengths.propsFor('supplierSku')}
                    />
                    <TextField
                      label={t('itemSuppliers.assign.leadTime')}
                      value={form.leadTime}
                      onChange={(e) => setForm((prev) => ({ ...prev, leadTime: e.target.value }))}
                      size="small"
                      className="flex-1"
                      disabled={busy}
                      helperText={t('itemSuppliers.assign.leadTimeHelp')}
                    />
                  </Box>
                </>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {pickerShown && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Plus size={ICON.md} />}
                    disabled={!form.supplier || busy || assignLengths.tooLong}
                    loading={createMutation.isPending}
                    onClick={handleAssign}
                  >
                    {t('itemSuppliers.assign.button')}
                  </Button>
                )}
                {canCreateSupplier && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Plus size={ICON.md} />}
                    disabled={busy}
                    onClick={() => setCreateOpen(true)}
                  >
                    {t('supplierSetup.createSupplier')}
                  </Button>
                )}
              </Box>
            </>
          )}
          <DialogErrorBanner error={error} onClose={() => setError(null)} />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={close} disabled={busy}>{t('btn.close')}</Button>
        </DialogActions>
      </Dialog>
      {createOpen && (
        <CreateSupplierDialog
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
        />
      )}
      <ConfirmDialog {...dialogProps} />
    </>
  );
}
